import React, { useEffect, useState } from 'react';
import {
  useNavigate,
  useParams,
  useLocation,
  useSearchParams,
} from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { setQRCodeId } from 'app/state';
import { Typography, Button } from '@mui/material';
import TSButton from 'components/common/Button/Button';
import Link from '@mui/material/Link';

import { ROUTES } from 'constants/RoutePaths';
import { buildRoutePath } from 'utils/utility';

import { isUserAuth, userToken } from '_actions/user_actions';

import { signInWithRedirect } from 'aws-amplify/auth';

import { useGetTrailQuery } from 'app/api/trailApiSlice';
import MLModal from 'components/common/Modal/Modal';
import LogActivity from 'pages/Protected/Activity/LogActivity';
import NewPlan from 'pages/Protected/Plans/NewPlan';
import FlagRounded from '@mui/icons-material/FlagRounded';
import OpenInNewRounded from '@mui/icons-material/OpenInNewRounded';
import PlaceRounded from '@mui/icons-material/PlaceRounded';
import RouteRounded from '@mui/icons-material/RouteRounded';
import ScheduleRounded from '@mui/icons-material/ScheduleRounded';
import FactList from 'components/common/FactList/FactList';
import WeatherForecast from 'components/TrailSafe/WeatherForecast/WeatherForecast';
import DarkFinishWarning from 'components/TrailSafe/DarkFinishWarning/DarkFinishWarning';

const TrailDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();

  const [isAuth, setIsAuth] = useState(false);
  const [token, setToken] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPlanModalOpen, setIsPlanModalOpen] = useState(false);

  const { trailId } = useParams(); // Get trailId from URL parameters

  const QRCodeID = searchParams.get('QRCodeID');

  if (QRCodeID) {
    dispatch(setQRCodeId(QRCodeID));
  }

  useEffect(() => {
    const checkAuth = async () => {
      const t = await userToken();
      setIsAuth(!!t);
      setToken(t);
    };
    checkAuth();
    setIsLoading(false);
  }, []);

  const handleSignIn = () => {
    const path = location.pathname + location.search;
    localStorage.setItem('postSignInPath', path);
    signInWithRedirect();
  };

  const {
    data: trail,
    isLoading: isTrailLoading,
    error,
  } = useGetTrailQuery(trailId);

  if (error) {
    return (
      <div className="ts-page">
        {error.status === 404 ? 'Trail not found.' : 'Error loading trail.'}
      </div>
    );
  }

  if (isLoading || isTrailLoading) {
    return <div className="ts-page">Loading...</div>;
  } else {
    const defaultCheckinPoint = trail[0]?.trailcheckinpoints.find(
      (point) => point.DefaultStartPoint
    );
    return (
      <>
        {/* On a laptop the map and forecast sit beside the warnings and the
            Start buttons; on a phone it's one column in the same order. */}
        <div className="ts-page ts-page--wide flex flex-col gap-6">
          <header className="flex flex-col gap-3">
            <Typography variant="h2">{trail[0]?.trailname}</Typography>
            <FactList>
              <li>
                <PlaceRounded aria-hidden="true" />
                <span>{trail[0]?.parkname}</span>
              </li>
              <li>
                <RouteRounded aria-hidden="true" />
                <span>{(trail[0]?.distancemeters / 1000).toFixed(1)} km</span>
              </li>
              <li>
                <ScheduleRounded aria-hidden="true" />
                <span>About {trail[0]?.formattedtime}</span>
              </li>
            </FactList>
          </header>

          <div className="flex flex-col gap-6 lg:grid lg:grid-cols-2 lg:items-start">
            <div className="flex flex-col gap-6">
              <img
                className="w-full aspect-video object-cover rounded-[var(--radius-lg)] bg-[var(--surface-sunken)]"
                src={require(
                  `../../../../assets/LocationImages/${trail[0].mapimagelocation}`
                )}
                alt={`Map of ${trail[0].trailname}`}
              />

              <WeatherForecast trailId={trailId} />
            </div>

            <div className="flex flex-col gap-6">
              <DarkFinishWarning
                trailId={trailId}
                durationMinutes={trail[0]?.timeminutes}
              />

              {isAuth ? (
                <div className="flex flex-col gap-2">
                  <TSButton
                    btnText="Start now"
                    icon={<FlagRounded />}
                    onClickFunc={() => setIsModalOpen(true)}
                  />
                  <TSButton
                    btnText="Plan a walk"
                    btnVariant="outlined"
                    onClickFunc={() => setIsPlanModalOpen(true)}
                  />
                </div>
              ) : (
                <TSButton
                  btnText="Sign in to start or plan a walk"
                  onClickFunc={handleSignIn}
                />
              )}

              <Typography variant="body1">
                <Link
                  href={trail[0]?.parksinfourl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 !font-bold"
                >
                  More about this trail from Parks and Wildlife
                  <OpenInNewRounded fontSize="small" aria-hidden="true" />
                </Link>
              </Typography>
            </div>
          </div>
        </div>
        <MLModal open={isModalOpen} setOpenModal={setIsModalOpen}>
          <LogActivity
            option={trail[0]}
            isModal={true}
            toggleModal={setIsModalOpen}
          />
        </MLModal>
        <MLModal open={isPlanModalOpen} setOpenModal={setIsPlanModalOpen}>
          <NewPlan trail={trail[0]} toggleModal={setIsPlanModalOpen} />
        </MLModal>
      </>
    );
  }
};

export default TrailDetail;
